import type { Email } from '../email/emailTypes';
import type { User } from '../user/userTypes';
import {
	Business,
	BusinessQuery,
	DashboardAction,
	DashboardActionTypes as actionTypes,
	List,
	UserNotes
} from './dashboardTypes';

export const dashboardSetQuery = (payload: BusinessQuery): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_QUERY,
	payload
});

export const dashboardResetQuery = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_RESET_QUERY
});

export const dashboardResetQueryPartial = (
	payload: BusinessQuery
): DashboardAction => ({
	type: actionTypes.DASHBOARD_RESET_QUERY_PARTIAL,
	payload
});

export const dashboardSelectLocation = (payload: string): DashboardAction => ({
	type: actionTypes.DASHBOARD_SELECT_LOCATION,
	payload
});

export const dashboardSelectBusiness = (
	payload: Business | null
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SELECT_BUSINESS,
	payload
});

export const dashboardUnselectBusinesses = (
	payload: Business
): DashboardAction => ({
	type: actionTypes.DASHBOARD_UNSELECT_BUSINESSES,
	payload
});

export const dashboardGetBusinessesRequest = (
	payload: BusinessQuery
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESSES_REQUEST,
	payload
});

export const dashboardClearBusinesses = (payload: any): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLEAR_BUSINESSES,
	payload
});

export const dashboardClearCursors = () => ({
	type: actionTypes.DASHBOARD_CLEAR_CURSORS
});

export const dashboardGetBusinessRequest = (payload: any): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESS_REQUEST,
	payload
});

export const dashboardGetBusinessSuccess = (
	payload: Business[]
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESS_SUCCESS,
	payload
});

export const dashboardSetBusinessesCount = (
	payload: number
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_BUSINESSES_COUNT,
	payload
});

export type DashboardGetBusinessesSuccessProps = {
	data: { result: Business[]; count: number; cursor: [number] };
};
export const dashboardGetBusinessesSuccess = (
	payload: DashboardGetBusinessesSuccessProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESSES_SUCCESS,
	payload
});

export const dashboardGetBusinessesFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESSES_FAILURE,
	error
});

export const dashboardGetAllBusinesses = (
	payload: List['id']
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ALL_BUSINESSES,
	payload
});

export const dashboardGetAllBusinessesLoading = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ALL_BUSINESSES_LOADING
});

export const dashboardGetAllBusinessesSuccess = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ALL_BUSINESSES_SUCCESS,
	payload
});

export const dashboardGetSelectBusiness = (payload: any): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SELECT_BUSINESS,
	payload
});

export const dashboardGetSelectBusinessSuccess = (
	payload: Business[]
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SELECT_BUSINESS_SUCCESS,
	payload
});

export const dashboardUpdateBusinessNotesRequest = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_REQUEST,
	payload
});

export const dashboardUpdateBusinessNotesSuccess = (
	payload: Business
): DashboardAction => ({
	type: actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_SUCCESS,
	payload
});

export const dashboardUpdateBusinessNotesFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_UPDATE_BUSINESS_NOTES_FAILURE,
	error
});

export const dashboardSelectList = (payload: string): DashboardAction => ({
	type: actionTypes.DASHBOARD_SELECT_LIST,
	payload
});

export const dashboardGetListsRequest = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_LISTS_REQUEST
});

export const dashboardGetListsSuccess = (payload: List[]): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_LISTS_SUCCESS,
	payload
});

export const dashboardGetListsFailure = (error: Error): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_LISTS_FAILURE,
	error
});

export const dashboardGetSubordinateListsRequest = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SUBORDINATE_LISTS_REQUEST,
	payload
});

export const dashboardGetSubordinateListsSuccess = (
	payload: List[]
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SUBORDINATE_LISTS_SUCCESS,
	payload
});

export const dashboardGetSubordinateListsFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SUBORDINATE_LISTS_FAILURE,
	error
});

export const dashboardAddListRequest = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_LIST_REQUEST,
	payload
});

export const dashboardAddListLoading = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_LIST_LOADING,
	payload
});

export const dashboardSetToStale = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_LIST_STALE
});

export const dashboardAddListSuccess = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_LIST_SUCCESS,
	payload
});

export const dashboardAddListFailure = (error: Error): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_LIST_FAILURE,
	error
});

export const dashboardEditListRequest = (
	payload: { listId: List['id'] } & List
): DashboardAction => ({
	type: actionTypes.DASHBOARD_EDIT_LIST_REQUEST,
	payload
});

export const dashboardEditListSuccess = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_EDIT_LIST_SUCCESS,
	payload
});

export const dashboardEditListFailure = (error: Error): DashboardAction => ({
	type: actionTypes.DASHBOARD_EDIT_LIST_FAILURE,
	error
});

export const dashboardAddBusinessToListRequest = (payload: {
	businessIds: string[];
	listId: string;
	selectedLocation?: string;
}): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_REQUEST,
	payload
});

export const dashboardAddBusinessToListSuccess = (
	payload: List
): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_SUCCESS,
	payload
});

export const dashboardAddBusinessToListFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESS_TO_LIST_FAILURE,
	error
});

export const dashboardRemoveBusinessFromListRequest = (payload: {
	businessId: string;
	listId: string;
}): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_REQUEST,
	payload
});

export const dashboardRemoveBusinessFromListSuccess = (
	payload: List
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_SUCCESS,
	payload
});

export const dashboardRemoveBusinessFromListFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BUSINESS_FROM_LIST_FAILURE,
	error
});

export const dashboardRemoveListRequest = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_LIST_REQUEST,
	payload
});

export const dashboardRemoveListSuccess = (payload: List): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_LIST_SUCCESS,
	payload
});

export const dashboardRemoveListFailure = (error: Error): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_LIST_FAILURE,
	error
});

export const dashboardRemoveListsBatchRequest = (
	ids: Array<List['id']>
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_REQUEST,
	payload: { ids }
});

export const dashboardRemoveListsBatchSuccess = (
	ids: Array<List['id']>
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_SUCCESS,
	payload: { ids }
});

export const dashboardRemoveListsBatchFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_BATCH_LISTS_FAILURE,
	error
});

export const dashboardExportBusinessesRequest = (
	payload: BusinessQuery
): DashboardAction => ({
	type: actionTypes.DASHBOARD_EXPORT_BUSINESSES_REQUEST,
	payload
});

export const dashboardExportBusinessesSuccess = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_EXPORT_BUSINESSES_SUCCESS
});

export const setSentEmailsInBusinesses = (
	payload: Email[]
): DashboardAction => ({
	type: actionTypes.SET_SENT_EMAILS_IN_BUSINESSES,
	payload
});

export const dashboardExportBusinessesFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_EXPORT_BUSINESSES_FAILURE,
	error
});

export type DashboardSetBusinessReminderProps = {
	note: Business['note'];
	triggerDate: number;
	noteHtml: string;
	originalNote: Business['note'];
	businessId: Business['_id'];
	businessName: Business['name'];
	userEmail: User['email'];
	listId: Business['listId'];
};

export const dashboardSetBusinessReminder = (
	payload: DashboardSetBusinessReminderProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_BUSINESS_REMINDER,
	payload
});

export const dashboardSetBusinessReminderFailure = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_FAILURE,
	payload
});

export const dashboardSetBusinessReminderLoading = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_LOADING,
	payload
});

export const dashboardSetBusinessReminderSuccess = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_BUSINESS_REMINDER_SUCCESS,
	payload
});

export type DashboardDeleteBusinessReminderProps = {
	businessId: Business['_id'];
	businessName: string;
	userEmail: User['email'];
	listId: Business['listId'];
};

export const dashboardDeleteBusinessReminder = (
	payload: DashboardDeleteBusinessReminderProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_DELETE_BUSINESS_REMINDER,
	payload
});

export const dashboardDeleteBusinessReminderSuccess = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_DELETE_BUSINESS_REMINDER_SUCCESS,
	payload
});

export const dashboardResetBusinessReminder = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_RESET_BUSINESS_REMINDER
});

export const dashboardRemoveModalBusiness = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_MODAL_BUSINESS
});

export type DashboardAddBusinessDataProps = {
	type: 'web-link' | 'email' | 'phone';
	businessId: Business['_id'];
	userEmail: User['email'];
	mainData: string;
	linkData?: string;
	listId: Business['listId'];
};

export const dashboardAddBusinessData = (
	payload: DashboardAddBusinessDataProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESS_DATA,
	payload
});

export const dashboardAddBusinessDataSuccess = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_SUCCESS,
	payload
});

export const dashboardAddBusinessDataFailure = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_FAILURE,
	payload
});

export const dashboardAddBusinessDataLoading = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_LOADING
});

export const dashboardAddBusinessDataReset = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_BUSINESSES_DATA_RESET
});

export type DashboardDeleteBusinessDataProps = {
	businessId: Business['_id'];
	userEmail: User['email'];
	listId: Business['listId'];
	dataInd: number;
};

export const dashboardDeleteBusinessData = (
	payload: DashboardDeleteBusinessDataProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_DELETE_BUSINESSES_DATA,
	payload
});

export const dashboardDeleteBusinessDataSuccess = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_DELETE_BUSINESSES_DATA_SUCCESS,
	payload
});

export const dashboardGetMapBoxKey = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_MAPBOX_KEY
});

export const dashboardGetMapBoxKeySuccess = (
	payload: string
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_MAPBOX_KEY_SUCCESS,
	payload
});

export const dashboardGetSelectedList = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_SELECTED_LIST
});

export const dashboardSetSelectedList = (
	payload: List | null
): DashboardAction => ({
	type: actionTypes.DASHBOARD_SET_SELECTED_LIST,
	payload
});

export const dashboardAddOneToSaveList = (payload: any): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_ONE_TO_SAVE_LIST,
	payload
});

export const dashboardAddManyToSaveList = (payload: any): DashboardAction => ({
	type: actionTypes.DASHBOARD_ADD_MANY_TO_SAVE_LIST,
	payload
});

export const dashboardRemoveOneFromSaveList = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_ONE_FROM_SAVE_LIST,
	payload
});

export const dashboardRemoveManyFromSaveList = (
	payload: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_MANY_FROM_SAVE_LIST,
	payload
});

export const dashboardRemoveAllFromSaveList = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REMOVE_ALL_FROM_SAVE_LIST
});

export const dashboardCloseBusinessPreview = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLOSE_BUSINESS_PREVIEW,
	payload
});

export type DashboardGetCommunicationCenterBusinessesProps = {
	contacted: boolean;
	contactedMessenger: boolean;
	contactedInstagramMessenger: boolean;
	contactedPhone: boolean;
	listId?: string;
	repliedYes: boolean;
	repliedNo: boolean;
	includes: string[];
	excludes: string[];
	sort: Record<string, number>;
	limit: number;
	skip: number;
	returnCount: true;
	openCountZero: boolean;
	openCountOneFour: boolean;
	openCountFiveEight: boolean;
	openCountNinePlus: boolean;
	leadYes: boolean;
	leadNo: boolean;
	leadUnmarked: boolean;
	startDate: number;
	endDate: number;
};
export const dashboardGetCommunicationCenterBusinesses = (
	payload: DashboardGetCommunicationCenterBusinessesProps
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES,
	payload
});

export const dashboardGetCommunicationCenterBusinessesSuccess = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_COMMUNICATION_CENTER_BUSINESSES_SUCCESS,
	payload
});

export const dashboardGetBusinessOpenNote = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESS_OPEN_NOTE
});

export const dashboardGetBusinessCloseNote = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_BUSINESS_CLOSE_NOTE
});

export const dashboardOpenEmailPreview = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_OPEN_EMAIL_PREVIEW
});

export const dashboardOpenEmailPreviewClear = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_OPEN_EMAIL_PREVIEW_CLEAR
});

export const emailGetEmailThreadLoading = (): EmailAction => ({
	type: actionTypes.EMAIL_GET_EMAIL_THREAD_LOADING
});

export const emailGetEmailThreadSuccess = (payload): EmailAction => ({
	type: actionTypes.EMAIL_GET_EMAIL_THREAD_SUCCESS,
	payload
});

export const emailGetEmailThreadFailure = (): EmailAction => ({
	type: actionTypes.EMAIL_GET_EMAIL_THREAD_FAILURE
});

export const dashboardGetGooglePlaces = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_GOOGLE_PLACES,
	payload
});

export const dashboardGetGooglePlacesSuccess = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_GOOGLE_PLACES_SUCCESS,
	payload
});

export const dashboardGetGooglePlacesFailure = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_GOOGLE_PLACES_FAILURE
});

export const dashboardClearGooglePlaces = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLEAR_GOOGLE_PLACES
});

export const dashboardRequestNewLocation = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_REQUEST_NEW_LOCATION,
	payload
});

export const dashboardRequestNewLocationSuccess = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_SUCCESS
});

export const dashboardRequestNewLocationFailure = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_FAILURE
});

export const dashboardRequestNewLocationClearStatus = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REQUEST_NEW_LOCATION_CLEAR_STATUS
});

export const dashboardRequestAddLocationFeature = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE
});

export const dashboardRequestAddLocationFeatureSuccess =
	(): DashboardAction => ({
		type: actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_SUCCESS
	});

export const dashboardRequestAddLocationFeatureFailure =
	(): DashboardAction => ({
		type: actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_FAILURE
	});

export const dashboardRequestAddLocationFeatureClearStatus =
	(): DashboardAction => ({
		type: actionTypes.DASHBOARD_REQUEST_ADD_LOCATION_FEATURE_CLEAR_STATUS
	});

export const dashboardGetActivitiesRequest = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITIES,
	payload
});

export const dashboardGetActivitiesLoading = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITIES_LOADING
});

export const dashboardGetActivitiesSuccess = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITIES_SUCCESS,
	payload
});

export const dashboardGetActivitiesFailure = (
	error: Error
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITIES_FAILURE,
	error
});

export const dashboardCreateActivityNote = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE,
	payload
});

export const dashboardCreateActivityNoteLoading = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_LOADING
});

export const dashboardCreateActivityNoteSuccess = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_SUCCESS
});

export const dashboardCreateActivityNoteFailure = (
	error: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_FAILURE,
	error
});

export const dashboardCreateActivityNoteClear = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CREATE_ACTIVITY_NOTE_CLEAR
});

export const dashboardActivitySearch = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_ACTIVITY_SEARCH,
	payload
});

export const dashboardGetActivityEmailOutbound = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND,
	payload
});

export const dashboardGetActivityEmailOutboundLoading = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_LOADING,
	payload
});

export const dashboardGetActivityEmailOutboundSuccess = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_SUCCESS,
	payload
});

export const dashboardGetActivityEmailOutboundFailure = (
	error: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_OUTBOUND_FAILURE,
	error
});

export const dashboardGetActivityEmailInbound = (payload): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND,
	payload
});

export const dashboardGetActivityEmailInboundLoading = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_LOADING,
	payload
});

export const dashboardGetActivityEmailInboundSuccess = (
	payload
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_SUCCESS,
	payload
});

export const dashboardGetActivityEmailInboundFailure = (
	error: any
): DashboardAction => ({
	type: actionTypes.DASHBOARD_GET_ACTIVITY_EMAIL_INBOUND_FAILURE,
	error
});

export const dashboardClearActivityData = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLEAR_ACTIVITY_DATA
});

export const dashboardClickDeleteNote = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLICK_DELETE_NOTE
});

export const dashboardClickDeleteNoteClear = (): DashboardAction => ({
	type: actionTypes.DASHBOARD_CLICK_DELETE_NOTE_CLEAR
});

export const dashboardSelectBusinessTab = (tab: string): DashboardAction => ({
	type: actionTypes.DASHBOARD_SELECT_BUSINESS_TAB,
	payload: tab
});
